import { useEffect } from 'react';

import { InstallationOptionsEnum } from '@updater/consumer-graph';

import { Address, PiiTokenizedData, CreditCard } from 'flows/tvi/types';
import { useUserData } from 'flows/core/hooks/use-profile';
import {
  ActivationDefaults,
  useGetEstimatedActivationDate,
} from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { createFormStore } from '../common/form-store';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';
import { useReservation } from '@updater/ui-widgets';
import type { HomeServicesReservation } from '@updater/consumer-graph';

export type CoxFormData = {
  internetModemChoice: string;
  installation?: {
    internetModemChoice: string;
  };
  ssn: string | PiiTokenizedData;
  dob: string | PiiTokenizedData;
  passcode: string | PiiTokenizedData;
  securityQuestion: string;
  securityAnswer: string | PiiTokenizedData;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  shippingAddress?: string;
  newShippingAddress?: Address;
  installationDate?: string;
  installationTimeWindow?:
    | '8am to 10am'
    | '10am to 12pm'
    | '1pm to 3pm'
    | '3pm to 5pm';
  installationType?: InstallationOptionsEnum;
  creditCard?: Partial<CreditCard>;
  /*
  This field is not UI visible
  It is used by informant to conditionally render the `Schedule Installation` Screen
  based if the user has self install enabled or not
*/
  priorAddress?: { address: Address };
  isInstantOn?: boolean;
};

export enum CoxPages {
  MODEM_CHOICE = 'modems',
  CURRENT_ADDRESS_LONGEVITY = 'current-address-longevity',
  IDENTITY = 'identity',
  SECURE_ACCOUNT = 'secure-account',
  SCHEDULE_INSTALLATION = 'schedule-installation',
  PAYMENT_OPTION = 'payment-option',
  PAYMENT = 'payment-method',
  REVIEW = 'buy/review',
  SUBMISSION = 'submission',
  ERROR = 'error',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'passcode',
  'securityQuestion',
  'securityAnswer',
  'creditCard',
];

const partialFormDataFromReservation = (
  reservation: HomeServicesReservation,
  activationDefaults: ActivationDefaults
) => {
  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;
  const defaultInstallWindowTime = activationDefaults.defaultTimeWindow;

  return {
    installationType:
      (installationTypeFromReservation as InstallationOptionsEnum) ??
      InstallationOptionsEnum.self,
    installationTimeWindow: defaultInstallWindowTime,
  };
};

const useStore = createFormStore<CoxFormData, CoxPages>({
  provider: 'cox',
  initialPageRoute: CoxPages.MODEM_CHOICE,
  initialFormData: {},
});

export function getCoxFormState() {
  return useStore.getState();
}

export const useCoxStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const { loading: userDataLoading, user } = useUserData();
  const activationDefaults = useGetEstimatedActivationDate();
  const reservationData = useReservation({
    moveId: user?.currentMove?.id ?? '',
  });

  const hasHydrated = useStore.persist.hasHydrated();

  useEffect(() => {
    if (
      hasHydrated &&
      status !== 'loaded' &&
      !userDataLoading &&
      !reservationData?.loading
    ) {
      const installationTypeFromReservation =
        (reservationData?.offer?.options?.installationOptions
          ?.type as InstallationOptionsEnum) ?? InstallationOptionsEnum.self;
      const isInstantOn = Boolean(
        reservationData?.offer?.planDetails?.instantOn
      );

      const userFirstName = user?.firstName;
      const userLastName = user?.lastName;
      const userEmail = user?.email;
      const userPhoneNumber = user?.phone;
      const { fromAddress } = user?.currentMove || {};

      const initialData: Partial<CoxFormData> = {
        ...partialFormDataFromReservation(reservationData, activationDefaults),
        installationDate: activationDefaults.estimatedDate ?? '',
        installationType: installationTypeFromReservation,
        firstName: userFirstName,
        lastName: userLastName,
        email: userEmail,
        phone: userPhoneNumber,
        smsOptIn: [SMS_OPT_IN_VALUE],
        isInstantOn,
        creditCard: {
          firstName: userFirstName,
          lastName: userLastName,
          address: fromAddress,
        },
      };

      initializeFormData({
        formData: initialData,
        offerName: reservationData?.offer?.name,
      });
    }
  }, [
    hasHydrated,
    status,
    userDataLoading,
    user,
    initializeFormData,
    reservationData,
    activationDefaults,
  ]);

  return useStore(...args);
};
